import { create } from 'zustand';
import {
  type Challenge,
  type ChallengeProgress,
  freshProgress,
  pickToday,
  todayKey,
} from '../dailyChallenge';
import { audio } from '../game/audio/AudioEngine';

const NAME_KEY = 'cellsnake:playerName';
const HUE_KEY = 'cellsnake:chosenHue';
const PATTERN_KEY = 'cellsnake:chosenPattern';
const HISTORY_KEY = 'cellsnake:matchHistory';
const HISTORY_LIMIT = 5;

const VALID_PATTERNS = ['plasma', 'glitch', 'geometric', 'bio'] as const;
export type PersistedPattern = (typeof VALID_PATTERNS)[number];

function loadPattern(): PersistedPattern {
  try {
    const raw = localStorage.getItem(PATTERN_KEY) as PersistedPattern | null;
    return raw && (VALID_PATTERNS as readonly string[]).includes(raw) ? raw : 'plasma';
  } catch { return 'plasma'; }
}
function savePattern(p: PersistedPattern): void {
  try { localStorage.setItem(PATTERN_KEY, p); } catch { /* noop */ }
}
const ACH_KEY = 'cellsnake:achievements';
const STREAK_KEY = 'cellsnake:dailyStreak';
const CHALLENGE_KEY = 'cellsnake:dailyChallenge';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function loadAchievements(): string[] {
  try {
    const raw = localStorage.getItem(ACH_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveAchievements(ids: string[]): void {
  try { localStorage.setItem(ACH_KEY, JSON.stringify(ids)); } catch { /* noop */ }
}
interface StreakRecord {
  /** ISO date of the last day the player visited. */
  lastDate: string;
  /** Consecutive-day count. Resets to 1 if the player skips a day. */
  count: number;
}
function loadStreak(): StreakRecord {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { lastDate: '', count: 0 };
    return JSON.parse(raw) as StreakRecord;
  } catch { return { lastDate: '', count: 0 }; }
}
function saveStreak(s: StreakRecord): void {
  try { localStorage.setItem(STREAK_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

/** Load today's challenge progress. Auto-resets if the date has rolled
 *  over OR if the persisted challenge id no longer matches today's pick
 *  (defensive — if we ship a new CATALOG that bumps yesterday's index). */
function loadChallengeProgress(): { challenge: Challenge; progress: ChallengeProgress } {
  const today = pickToday();
  try {
    const raw = localStorage.getItem(CHALLENGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as ChallengeProgress;
      if (stored.dateKey === todayKey() && stored.id === today.id) {
        return { challenge: today, progress: stored };
      }
    }
  } catch { /* private mode / parse error */ }
  return { challenge: today, progress: freshProgress(today) };
}
function saveChallengeProgress(p: ChallengeProgress): void {
  try { localStorage.setItem(CHALLENGE_KEY, JSON.stringify(p)); } catch { /* noop */ }
}

export interface MatchResult {
  /** Unix ms when the match ended. */
  endedAt: number;
  /** Final mass at death. */
  mass: number;
  /** Kills scored this life. */
  kills: number;
  /** Player hue this match. */
  hue: number;
}

function loadHistory(): MatchResult[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as MatchResult[];
    return Array.isArray(arr) ? arr.slice(0, HISTORY_LIMIT) : [];
  } catch { return []; }
}
function saveHistory(rows: MatchResult[]): void {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(rows.slice(0, HISTORY_LIMIT))); }
  catch { /* noop */ }
}

function loadName(): string {
  try { return localStorage.getItem(NAME_KEY) ?? ''; }
  catch { return ''; }
}
function saveName(name: string): void {
  try { localStorage.setItem(NAME_KEY, name); }
  catch { /* noop */ }
}
/** Game-canonical hue palette. Must mirror TEAM_HUES in game/constants.ts —
 *  duplicated here so the store doesn't reach into the game module (clean
 *  layering). Snake's color-match scoring only counts a pellet as PERFECT
 *  within 18° of the snake's hue, and pellets only spawn at TEAM_HUES, so
 *  picking any hue off this list would break the eating effects entirely. */
const TEAM_HUES = [20, 65, 110, 155, 200, 245, 290, 335] as const;

/** Snap an arbitrary hue (0-359) to the nearest TEAM_HUE. Honors the
 *  circular nature of hue (350° is closer to 20° than 110°). */
function snapToTeamHue(raw: number): number {
  let best: number = TEAM_HUES[0];
  let bestD = Infinity;
  for (const h of TEAM_HUES) {
    const d = Math.min(Math.abs(raw - h), 360 - Math.abs(raw - h));
    if (d < bestD) { bestD = d; best = h; }
  }
  return best;
}

function loadHue(): number {
  try {
    const v = Number(localStorage.getItem(HUE_KEY));
    if (!Number.isFinite(v) || v < 0 || v >= 360) return 200;
    // Migrate any persisted off-spec hue from older builds (slider days)
    // to the nearest TEAM_HUE so eating effects work.
    return snapToTeamHue(v);
  } catch { return 200; }
}
function saveHue(h: number): void {
  try { localStorage.setItem(HUE_KEY, String(Math.round(h))); }
  catch { /* noop */ }
}

/**
 * Zustand store for HUD-visible state only.
 *
 * Per-frame engine state stays inside engine classes for perf. The engine
 * pushes throttled snapshots into the store. The store also exposes the
 * respawn callback that React components use to re-init the player.
 */
export interface HudState {
  fps: number;
  mass: number;
  radius: number;
  posX: number;
  posY: number;
  zoom: number;
  alive: boolean;
  playerHue: number;
  combo: number;
  bestMass: number;
  muted: boolean;
  paused: boolean;
  /** Audio engine state — true until the user gestures and AudioContext resumes. */
  audioLocked: boolean;
  /** Remaining seconds of free-boost from a Surge pickup (0 = inactive). */
  surgeRemaining: number;
  /** Remaining seconds of 2× pellet mass from a Double pickup. */
  doubleRemaining: number;
  /** Whether the player currently holds an unused Aegis shield. */
  shielded: boolean;
  /** Kills the player has scored in the current life. */
  kills: number;
  /** Seconds remaining in the current combo window. */
  comboRemaining: number;
  /** Set when the player dies — surfaced by DeathModal as "killed by X". */
  killedByName: string | null;
  killedByHue: number | null;
  /** Death-recap window. While `recapActive`, the HUD renders the
   *  cinematic overlay and the DeathModal stays hidden. */
  recapActive: boolean;
  recapKillerName: string | null;
  recapKillerHue: number | null;
  recapRank: number;
  recapFinalMass: number;
  /** Seconds the player has been alive in their current life — 0 when
   *  dead. HUD components read this to drive timing milestones
   *  (60s-reward anticipation, achievement progress). */
  aliveSeconds: number;
}

export interface LeaderRow {
  /** Numeric in offline mode (Snake's internal id), string in live
   *  mode (Colyseus session id). Consumed only as a React list key. */
  id: number | string;
  name: string;
  mass: number;
  isPlayer: boolean;
  hue: number;
  playerRank?: number | null;
}

interface GameStore {
  hud: HudState;
  leaderboard: LeaderRow[];
  respawn: (() => void) | null;
  toggleMute: (() => boolean) | null;
  togglePause: (() => void) | null;
  /** Skip the death-recap cinematic. Bound to engine.skipRecap(). */
  skipRecap: (() => void) | null;
  /** Mobile-HUD bindings. Engine writes them on mount. */
  setBoost: ((on: boolean) => void) | null;
  queueEject: (() => void) | null;
  /** Custom player name (persisted in localStorage). Empty = unset. */
  playerName: string;
  setPlayerName: (name: string) => void;
  /** Player's chosen hue (0-360). Persisted; defaults to cyan 200. */
  chosenHue: number;
  setChosenHue: (hue: number) => void;
  /** Transient hover-preview hue — set when the cursor is over a swatch,
   *  cleared on leave. Doesn't persist. PlayerSnakePreview prefers this
   *  over chosenHue so the snake updates live as the user scans options. */
  hoverHue: number | null;
  setHoverHue: (hue: number | null) => void;
  /** Player's chosen skin pattern. Persisted; defaults to 'plasma'. */
  chosenPattern: PersistedPattern;
  setChosenPattern: (p: PersistedPattern) => void;
  /** Transient hover-preview pattern — same semantics as `hoverHue`. */
  hoverPattern: PersistedPattern | null;
  setHoverPattern: (p: PersistedPattern | null) => void;
  /** Last N completed matches (most recent first). Persisted. */
  matchHistory: MatchResult[];
  pushMatch: (m: MatchResult) => void;
  /** Ids of unlocked achievements (persisted). */
  unlockedAchievements: string[];
  /** Most-recently-unlocked id, transient — drives the in-HUD toast. */
  lastAchievement: string | null;
  unlockAchievements: (ids: string[]) => void;
  clearLastAchievement: () => void;
  /** Daily-login streak (persisted). Updated by `bumpDailyStreak()`. */
  dailyStreak: number;
  bumpDailyStreak: () => void;
  /** Today's challenge — refreshes automatically when the date rolls over. */
  dailyChallenge: Challenge;
  dailyProgress: ChallengeProgress;
  /** Update progress toward the active challenge. Engine HUD-watcher calls
   *  this on relevant events (mass tick, kill, combo step, survive timer). */
  updateChallengeProgress: (value: number) => void;
  /** Manually refresh — called on Home mount in case the user crossed
   *  midnight while the tab was open. */
  refreshDailyChallenge: () => void;
  /** Realtime-server connection status — written by LivePresencePanel,
   *  read by the floating utility bar's status dot. */
  netStatus: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error' | 'full' | 'spectating';
  /** High-level game mode the player is in. Set by the matchmaking gate
   *  when the user picks a path; read by the HUD to render the right
   *  badge (LIVE / SPECTATING / TRAINING) + the "find live match"
   *  upgrade CTA. Independent of netStatus because a 'training' player
   *  is intentionally disconnected, not in error state. */
  gameMode: 'live' | 'training' | 'spectating';
  setGameMode: (m: GameStore['gameMode']) => void;
  setNetStatus: (s: GameStore['netStatus']) => void;
  /** Currently-joined room's shareable code (or null). */
  netRoomCode: string | null;
  setNetRoomCode: (c: string | null) => void;
  /** Friendly arena name of the joined room (e.g. "Aurora Drift").
   *  Surfaced in the HUD so the player knows WHERE they are. Null
   *  when offline / training. */
  netArenaName: string | null;
  setNetArenaName: (n: string | null) => void;
  /** Live player count in the joined Colyseus room. 0 when offline.
   *  Read by PlayerCountBadge so the landing's social-proof pill uses
   *  real numbers when the realtime backend is reachable. */
  netPlayers: number;
  setNetPlayers: (n: number) => void;
  /** Fires once per browser session when the player stays alive 60s in
   *  a single life — the "$20 Reward" retention beat. Reset on reload,
   *  NOT on respawn. Engine flips this; HUD reads it to mount the
   *  RewardUnlockedModal exactly once. */
  rewardFiredThisSession: boolean;
  fireSessionReward: () => void;
  /** Gold-border cosmetic granted by the 60s reward — persists for the
   *  rest of the session (independent of respawns). SnakeRenderer reads
   *  it to draw the gold halo around the player's body. */
  goldBorderActive: boolean;
  /** Visual FX intensity 0..1 — multiplier on sparkles density + LaserFlow
   *  opacity. Persisted; default 1.0. Settings modal owns the slider. */
  fxIntensity: number;
  setFxIntensity: (v: number) => void;
  /** Master audio volume 0..1. Persisted in the AudioEngine itself; this
   *  store cell mirrors it for React re-renders. */
  masterVolume: number;
  setMasterVolume: (v: number) => void;
  /** Music sub-mix volume 0..1 — independent of master. */
  musicVolume: number;
  setMusicVolume: (v: number) => void;
  /** Music-only mute toggle. Master mute trumps this. */
  musicMuted: boolean;
  toggleMusicMuted: () => void;
  /** Landing theme — 'dark' (default neon) or 'light' (high contrast).
   *  The game canvas always stays dark; this only affects landing UI. */
  theme: 'dark' | 'light';
  toggleTheme: () => void;
  /** Wipes progress-related localStorage keys (match history, achievements,
   *  streak, daily challenge progress, daily winner, onboarding flags,
   *  custom name/hue/pattern) and forces a full page reload so every
   *  component picks up the cleared state cleanly. Preserves audio,
   *  theme, and FX-intensity preferences — those are user comfort
   *  settings, not progress. */
  resetProgress: () => void;
  setHud: (h: Partial<HudState>) => void;
  setLeaderboard: (rows: LeaderRow[]) => void;
  setRespawn: (fn: (() => void) | null) => void;
  setToggleMute: (fn: (() => boolean) | null) => void;
  setTogglePause: (fn: (() => void) | null) => void;
  setSkipRecap: (fn: (() => void) | null) => void;
  setMobileControls: (set: ((on: boolean) => void) | null, eject: (() => void) | null) => void;
  /** Set the mobile virtual-joystick steering callback. Called by
   *  GameCanvas when the engine mounts; consumed by the VirtualJoystick
   *  HUD component. `angle` is in radians (world space) or null when
   *  the player isn't touching the joystick. */
  setMobileSteer: ((angle: number | null) => void) | null;
  setMobileSteerFn: (fn: ((angle: number | null) => void) | null) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  hud: {
    fps: 0,
    mass: 0,
    radius: 0,
    posX: 0,
    posY: 0,
    zoom: 1,
    alive: true,
    playerHue: 200,
    combo: 0,
    bestMass: 0,
    muted: false,
    paused: false,
    audioLocked: true,
    surgeRemaining: 0,
    doubleRemaining: 0,
    shielded: false,
    kills: 0,
    comboRemaining: 0,
    killedByName: null,
    killedByHue: null,
    recapActive: false,
    recapKillerName: null,
    recapKillerHue: null,
    recapRank: 0,
    recapFinalMass: 0,
    aliveSeconds: 0,
  },
  leaderboard: [],
  respawn: null,
  toggleMute: null,
  togglePause: null,
  skipRecap: null,
  setBoost: null,
  queueEject: null,
  playerName: loadName(),
  setPlayerName: (name) => {
    const trimmed = name.trim().slice(0, 16);
    saveName(trimmed);
    set({ playerName: trimmed });
  },
  chosenHue: loadHue(),
  setChosenHue: (hue) => {
    // ALWAYS snap to a team hue so the player's snake always has matching
    // pellets in the world. Without this, eating effects (perfect-match
    // particle burst + sound + celebration) silently disappear.
    const wrapped = ((Math.round(hue) % 360) + 360) % 360;
    const snapped = snapToTeamHue(wrapped);
    saveHue(snapped);
    set({ chosenHue: snapped });
  },
  hoverHue: null,
  setHoverHue: (hue) => set({ hoverHue: hue }),
  chosenPattern: loadPattern(),
  setChosenPattern: (p) => {
    savePattern(p);
    set({ chosenPattern: p });
  },
  hoverPattern: null,
  setHoverPattern: (p) => set({ hoverPattern: p }),
  matchHistory: loadHistory(),
  pushMatch: (m) => {
    set((s) => {
      const next = [m, ...s.matchHistory].slice(0, HISTORY_LIMIT);
      saveHistory(next);
      return { matchHistory: next };
    });
  },
  unlockedAchievements: loadAchievements(),
  lastAchievement: null,
  unlockAchievements: (ids) => {
    if (ids.length === 0) return;
    set((s) => {
      const set2 = new Set(s.unlockedAchievements);
      const newOnes: string[] = [];
      for (const id of ids) {
        if (!set2.has(id)) {
          set2.add(id);
          newOnes.push(id);
        }
      }
      if (newOnes.length === 0) return {};
      const next = [...set2];
      saveAchievements(next);
      return {
        unlockedAchievements: next,
        lastAchievement: newOnes[newOnes.length - 1] ?? null,
      };
    });
  },
  clearLastAchievement: () => set({ lastAchievement: null }),
  ...(() => {
    const { challenge, progress } = loadChallengeProgress();
    return { dailyChallenge: challenge, dailyProgress: progress };
  })(),
  updateChallengeProgress: (value) => {
    set((s) => {
      const goal = s.dailyChallenge.goal;
      // Monotonic — we never go backwards within a day (a player who hit
      // 5000 mass and then died still kept the ×5 combo / mass milestone).
      const next: ChallengeProgress = {
        ...s.dailyProgress,
        current: Math.max(s.dailyProgress.current, value),
        completed: s.dailyProgress.completed || value >= goal,
        completedAt:
          s.dailyProgress.completedAt
            ?? (value >= goal ? Date.now() : null),
      };
      if (
        next.current === s.dailyProgress.current &&
        next.completed === s.dailyProgress.completed
      ) {
        return {}; // no change, skip the set
      }
      saveChallengeProgress(next);
      return { dailyProgress: next };
    });
  },
  refreshDailyChallenge: () => {
    set((s) => {
      const today = pickToday();
      if (today.id === s.dailyChallenge.id && s.dailyProgress.dateKey === todayKey()) {
        return {}; // still fresh
      }
      const progress = freshProgress(today);
      saveChallengeProgress(progress);
      return { dailyChallenge: today, dailyProgress: progress };
    });
  },
  dailyStreak: loadStreak().count,
  bumpDailyStreak: () => {
    const prev = loadStreak();
    const today = todayISO();
    if (prev.lastDate === today) {
      // Already counted today.
      return;
    }
    const yest = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
    const next: StreakRecord = {
      lastDate: today,
      count: prev.lastDate === yest ? prev.count + 1 : 1,
    };
    saveStreak(next);
    set({ dailyStreak: next.count });
  },
  setHud: (h) => set((s) => ({ hud: { ...s.hud, ...h } })),
  setLeaderboard: (rows) => set({ leaderboard: rows }),
  setRespawn: (fn) => set({ respawn: fn }),
  setToggleMute: (fn) => set({ toggleMute: fn }),
  setTogglePause: (fn) => set({ togglePause: fn }),
  setSkipRecap: (fn) => set({ skipRecap: fn }),
  setMobileControls: (boostFn, ejectFn) => set({ setBoost: boostFn, queueEject: ejectFn }),
  setMobileSteer: null,
  setMobileSteerFn: (fn) => set({ setMobileSteer: fn }),
  netStatus: 'idle',
  setNetStatus: (s) => set({ netStatus: s }),
  netRoomCode: null,
  setNetRoomCode: (c) => set({ netRoomCode: c }),
  netArenaName: null,
  setNetArenaName: (n) => set({ netArenaName: n }),
  netPlayers: 0,
  setNetPlayers: (n) => set({ netPlayers: n }),
  // Default to 'training' so an offline / pre-connect first paint
  // reads as "you're in training" instead of falsely claiming "live".
  // The matchmaking gate flips this to 'live' or 'spectating' on a
  // successful connect.
  gameMode: 'training',
  setGameMode: (m) => set({ gameMode: m }),
  rewardFiredThisSession: false,
  goldBorderActive: false,
  fireSessionReward: () => set((s) => {
    // Idempotent — engine may call multiple times across frames before
    // the flag's effect propagates. Only first call wins.
    if (s.rewardFiredThisSession) return s;
    return { rewardFiredThisSession: true, goldBorderActive: true };
  }),
  fxIntensity: loadFx(),
  setFxIntensity: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    try { localStorage.setItem('cellsnake:fxIntensity', String(clamped)); } catch { /* noop */ }
    set({ fxIntensity: clamped });
  },
  masterVolume: loadVolume(),
  setMasterVolume: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    try { localStorage.setItem('cellsnake:masterVolume', String(clamped)); } catch { /* noop */ }
    // Apply live to the singleton AudioEngine so changes are audible
    // without needing to re-init the audio graph.
    audio.setVolume(clamped);
    set({ masterVolume: clamped });
  },
  musicVolume: audio.getMusicVolume(),
  setMusicVolume: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    audio.setMusicVolume(clamped);
    set({ musicVolume: clamped });
  },
  musicMuted: audio.isMusicMuted(),
  toggleMusicMuted: () => {
    const next = !audio.isMusicMuted();
    audio.setMusicMuted(next);
    set({ musicMuted: next });
  },
  theme: loadTheme(),
  toggleTheme: () => {
    set((s) => {
      const next: 'dark' | 'light' = s.theme === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('cellsnake:theme', next); } catch { /* noop */ }
      applyThemeClass(next);
      return { theme: next };
    });
  },
  resetProgress: () => {
    // Keys to wipe: progress + onboarding flags + identity. Preserves
    // user-comfort settings (theme, master/music volume, fx intensity)
    // so the player doesn't have to re-tune the experience after a
    // fresh start. The hard reload after clearing guarantees every
    // component re-reads its initial state from a clean slate.
    const PROGRESS_KEYS = [
      'cellsnake:playerName',
      'cellsnake:chosenHue',
      'cellsnake:chosenPattern',
      'cellsnake:matchHistory',
      'cellsnake:achievements',
      'cellsnake:dailyStreak',
      'cellsnake:dailyChallenge',
      'cellsnake:dailyWinner',
      'cellsnake:seenFirstPlayTutorial',
      'cellsnake:seenLoginModal',
      'cellsnake:seenSessionReward',
      // All-time best mass — the headline progress stat. Previously
      // survived resetProgress (defined in GameEngine.ts as
      // BEST_MASS_KEY) which let an old best ghost the "fresh start"
      // intent.
      'cellsnake:bestMass',
    ];
    try {
      for (const k of PROGRESS_KEYS) localStorage.removeItem(k);
    } catch { /* private mode — UX is the same on next reload */ }
    try { window.location.reload(); } catch { /* SSR / harness */ }
  },
}));

function loadTheme(): 'dark' | 'light' {
  try {
    const raw = localStorage.getItem('cellsnake:theme');
    const t = raw === 'light' ? 'light' : 'dark';
    applyThemeClass(t);
    return t;
  } catch {
    return 'dark';
  }
}
function applyThemeClass(t: 'dark' | 'light'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('nx-light', t === 'light');
}

function loadVolume(): number {
  try {
    const raw = localStorage.getItem('cellsnake:masterVolume');
    if (raw === null) return 0.7;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.7;
  } catch { return 0.7; }
}

function loadFx(): number {
  try {
    // Null-safe: see `loadVolume` in AudioEngine for the same bug fix.
    const raw = localStorage.getItem('cellsnake:fxIntensity');
    if (raw === null) return 1;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
  } catch { return 1; }
}
